import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { httpClient } from "infra/axios/axios-api";
import Spinner from "presentation/components/Shared/Spinner";

/**
 * Propriedades para o componente PdfViewerDialog.
 */
interface PdfViewerDialogProps {
  /** Função de callback chamada quando o diálogo é fechado. */
  onHide: () => void;
  /** O caminho do S3 para o ficheiro PDF a ser exibido. */
  s3Path: string | null;
}

/**
 * Um componente de diálogo reutilizável para exibir ficheiros PDF.
 * Ele encapsula a lógica de renderização de um PDF dentro de um <iframe>
 * e o gerenciamento da limpeza da URL do objeto Blob.
 */
const PdfViewerDialog: React.FC<PdfViewerDialogProps> = ({
  onHide,
  s3Path,
}) => {
  const [internalPdfUrl, setInternalPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchPdf = async () => {
      if (s3Path) {
        setIsLoadingPdf(true);
        setErrorPdf(null);
        setInternalPdfUrl(null); // Garante que o PDF antigo não é exibido durante o carregamento
        try {
          const fileStreamResponse = await httpClient.get(
            `/assets/download/${s3Path}`,
            {
              responseType: "blob",
            }
          );

          const pdfBlob = new Blob([fileStreamResponse.data], {
            type: "application/pdf",
          });
          objectUrl = URL.createObjectURL(pdfBlob);
          setInternalPdfUrl(objectUrl);
        } catch (err) {
          console.error("Erro ao carregar PDF do S3:", err);
          setErrorPdf("Não foi possível carregar o PDF.");
        } finally {
          setIsLoadingPdf(false);
        }
      }
    };

    fetchPdf();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [s3Path]);

  const handleHide = () => {
    onHide();
  };

  return (
    <Dialog
      header="Visualizador de Relatório"
      visible={!!s3Path}
      style={{ width: "70vw", height: "90vh" }}
      onHide={handleHide}
      maximizable
    >
      {isLoadingPdf && <Spinner />}
      {errorPdf && <div className="p-error">{errorPdf}</div>}
      {!isLoadingPdf && !errorPdf && internalPdfUrl && (
        <iframe
          src={internalPdfUrl}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Visualizador de PDF"
        />
      )}
      {!isLoadingPdf && !s3Path && <div>Nenhum PDF para exibir.</div>}
    </Dialog>
  );
};

export default PdfViewerDialog;
